import React, { useState, useEffect, useCallback } from 'react';
import Lottie from 'lottie-react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Table,
   Thead,
   Tr,
   Th,
   Tbody,
   Td,
   Checkbox,
   Text,
   Button,
   Flex,
   Select,
   FormLabel,
   useToast,
   Image,
   useColorModeValue,
} from '@chakra-ui/react';
import { MdOutlineKeyboardArrowRight, MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { updateUserRights } from '../../../redux/actions/user';
import { getUser } from '../../../redux/actions/user';
import { useDispatch } from 'react-redux';
import loaderAnimation from '../../../assets/img/auth/loding.gif';
import { userRightsData } from './UserRightData';
import { setAllPermissionsTrue, setAllActionPermissions } from '../../../utils/util';

const ManageUserRoleForm = ({ isOpen, onClose, selectedMember, companyList, processedData, onSaveUserRights }) => {
   const [selectAll, setSelectAll] = useState(false);
   const [addAll, setAddAll] = useState(false);
   const [editAll, setEditAll] = useState(false);
   const [deleteAll, setDeleteAll] = useState(false);
   const [printAll, setPrintAll] = useState(false);
   const [viewAll, setViewAll] = useState(false);
   const [userRoles, setUserRoles] = useState([]);
   const [expandedRoles, setExpandedRoles] = useState({});
   const userId = localStorage.getItem('userId');
   const [selectedCompanyId, setSelectedCompanyId] = useState('');
   const [userRightsByCompany, setUserRightsByCompany] = useState({});
   const [loading, setLoading] = useState(true);
   const toast = useToast();
   const overlayBg = useColorModeValue('rgba(255, 255, 255, 0.8)', 'rgba(0, 0, 0, 0.8)');
   const dispatch = useDispatch();

   // Reset all state when modal closes or selectedMember changes
   const resetState = useCallback(() => {
      setSelectAll(false);
      setAddAll(false);
      setEditAll(false);
      setDeleteAll(false);
      setPrintAll(false);
      setViewAll(false);
      setUserRoles([]);
      setExpandedRoles({});
      setSelectedCompanyId('');
      setUserRightsByCompany({});
      setLoading(true);
   }, []);

   // Function to handle company change
   const handleCompChange = async (event) => {
      const selectedId = event.target.value;

      // First save the current company's rights
      if (selectedCompanyId) {
         const rights = flattenHierarchy(userRoles);
         setUserRightsByCompany((prev) => ({
            ...prev,
            [selectedCompanyId]: {
               ...rights,
               companyId: Number(selectedCompanyId),
            },
         }));
      }

      // Then switch to the new company
      setSelectedCompanyId(selectedId);

      // Load user rights for the selected company
      if (userRightsByCompany[selectedId]) {
         setUserRoles(buildRoleHierarchy(userRightsByCompany[selectedId]));
      } else {
         setUserRoles(buildRoleHierarchy(userRightsData[0]));
      }
   };

   // Build hierarchical role structure based on parentroleId
   const buildRoleHierarchy = useCallback((rolesObject) => {
      if (!rolesObject || typeof rolesObject !== 'object') {
         return [];
      }

      // Extract the companyId if it exists
      const { companyId, ...rolesData } = rolesObject;

      // Create a map for quick lookup
      const roleMap = new Map();

      // First pass: create all roles and store in map
      Object.entries(rolesData).forEach(([key, value]) => {
         const role =
            typeof value === 'object' && value !== null
               ? {
                  ...value,
                  roleName: value.roleName || key,
                  originalRoleName: key,
               }
               : {
                  roleId: typeof value === 'number' ? value : null,
                  roleName: key,
                  originalRoleName: key,
                  add: false,
                  edit: false,
                  deleteRole: false,
                  print: false,
                  view: false,
                  parentroleId: null,
               };

         roleMap.set(role.roleId, role);
      });

      // Second pass: build hierarchy in the order of userRightsData[0]
      const rootRoles = [];
      const processedRoles = new Set();

      // Get the order from userRightsData[0] (excluding companyId)
      const orderedRoles = Object.entries(userRightsData[0])
         .filter(([key]) => key !== 'companyId')
         .map(([key, value]) => ({
            key,
            roleId: typeof value === 'object' ? value.roleId : value,
         }));

      // Process roles in the defined order
      orderedRoles.forEach(({ key, roleId }) => {
         if (!roleMap.has(roleId)) return;

         const role = roleMap.get(roleId);
         if (processedRoles.has(roleId)) return;

         if (role.parentroleId) {
            const parent = roleMap.get(role.parentroleId);
            if (parent) {
               if (!parent.children) {
                  parent.children = [];
               }
               parent.children.push(role);
               processedRoles.add(roleId);
            }
         } else {
            rootRoles.push(role);
            processedRoles.add(roleId);
         }
      });

      return rootRoles;
   }, []);

   const flattenHierarchy = (hierarchy) => {
      const flatPermissions = {};

      // Get the order from userRightsData[0]
      const orderedRoles = Object.entries(userRightsData[0])
         .filter(([key]) => key !== 'companyId')
         .map(([key]) => key);

      const traverse = (nodes) => {
         nodes.forEach((node) => {
            flatPermissions[node.originalRoleName || node.roleName] = {
               roleId: node.roleId,
               roleName: node.roleName,
               add: node.add || false,
               edit: node.edit || false,
               deleteRole: node.deleteRole || false,
               print: node.print || false,
               view: node.view || false,
               parentroleId: node.parentroleId || null,
            };
            if (node.children) traverse(node.children);
         });
      };

      traverse(hierarchy);

      // Create a new object with properties in the correct order
      const orderedResult = {};
      orderedRoles.forEach((key) => {
         if (flatPermissions[key]) {
            orderedResult[key] = flatPermissions[key];
         }
      });

      // Add any remaining roles that might not be in the original order
      Object.keys(flatPermissions).forEach((key) => {
         if (!orderedResult[key]) {
            orderedResult[key] = flatPermissions[key];
         }
      });

      return orderedResult;
   };

   // Toggle visibility of children for a role
   const toggleChildrenVisibility = (roleId) => {
      setExpandedRoles((prev) => ({
         ...prev,
         [roleId]: !prev[roleId],
      }));
   };

   // Render a role row and its children recursively
   const renderRoleRow = (role, depth = 0) => {
      const hasChildren = role.children && role.children.length > 0;
      const isExpanded = expandedRoles[role.roleId];

      return (
         <React.Fragment key={role.roleId}>
            <Tr>
               <Td>
                  <Checkbox
                     mr="5px"
                     isChecked={role.add && role.edit && role.deleteRole && role.print && role.view}
                     onChange={(e) => {
                        handleRoleSelectAll(role.roleId, e.target.checked);
                     }}
                  />
               </Td>
               <Td>
                  <Flex align="center" pl={depth * 20} cursor={hasChildren ? 'pointer' : 'default'}>
                     {hasChildren && (
                        <span onClick={() => toggleChildrenVisibility(role.roleId)}>
                           {isExpanded ? <MdOutlineKeyboardArrowDown /> : <MdOutlineKeyboardArrowRight />}
                        </span>
                     )}
                     <Text ml={2}>{role.roleName}</Text>
                  </Flex>
               </Td>
               {['add', 'edit', 'deleteRole', 'print', 'view'].map((field) => (
                  <Td key={field}>
                     <Checkbox
                        mr="5px"
                        isChecked={role[field]}
                        onChange={(e) => handlePermissionChange(role.roleId, field, e.target.checked)}
                     />
                  </Td>
               ))}
            </Tr>

            {isExpanded && hasChildren && role.children.map((child) => renderRoleRow(child, depth + 1))}
         </React.Fragment>
      );
   };

   // Initialize company selection and organize user rights by company
   useEffect(() => {
      if (!isOpen) return;

      resetState();

      // Organize user rights by company ID
      const rightsByCompany = {};

      // For new users (no selectedMember or processedData with rights)
      if ((!selectedMember || !selectedMember.userRights) && (!processedData || !processedData.userRights)) {
         // Initialize with default rights for each company
         companyList?.forEach((company) => {
            rightsByCompany[company.id] = userRightsData[0];
         });
      } else {
         // Process user rights from selectedMember
         if (selectedMember?.userRights?.length > 0) {
            selectedMember.userRights.forEach((right) => {
               if (right.companyId) {
                  rightsByCompany[right.companyId] = right;
               }
            });
         }

         // Process user rights from processedData
         if (processedData?.userRights?.length > 0) {
            processedData.userRights.forEach((right) => {
               if (right.companyId) {
                  rightsByCompany[right.companyId] = right;
               }
            });
         }
      }

      setUserRightsByCompany(rightsByCompany);

      // Determine which company to select initially
      let initialCompanyId = '';

      // Try to get company ID from selectedMember
      if (selectedMember?.companyId) {
         initialCompanyId = String(selectedMember.companyId);
      }
      // Or from userRights
      else if (selectedMember?.userRights?.[0]?.companyId) {
         initialCompanyId = String(selectedMember.userRights[0].companyId);
      }
      // Or from processedData
      else if (processedData?.userRights?.[0]?.companyId) {
         initialCompanyId = String(processedData.userRights[0].companyId);
      }
      // Or just pick the first company from the list
      else if (companyList?.length > 0) {
         initialCompanyId = String(companyList[0].id || '');
      }

      if (initialCompanyId) {
         setSelectedCompanyId(initialCompanyId);
      }

      setLoading(false);
   }, [isOpen, selectedMember, processedData, companyList, resetState]);

   // Load user rights for the selected company
   useEffect(() => {
      if (!selectedCompanyId || loading) return;

      // Load user rights for the selected company
      if (userRightsByCompany[selectedCompanyId]) {
         setUserRoles(buildRoleHierarchy(userRightsByCompany[selectedCompanyId]));
      } else {
         // If no rights exist for this company, load default rights
         setUserRoles(buildRoleHierarchy(userRightsData[0]));
      }
   }, [selectedCompanyId, userRightsByCompany, loading, buildRoleHierarchy]);

   // update all roles
   const handlePermissionChange = (roleId, field, value) => {
      const updatePermissions = (roles) => {
         return roles.map((role) => {
            let updatedRole = { ...role };
            if (role.roleId === roleId) {
               updatedRole = {
                  ...updatedRole,
                  [field]: value,
               };
               if (role.children && role.children.length > 0) {
                  updatedRole.children = updatedRole.children.map((child) => ({
                     ...child,
                     [field]: value,
                  }));
               }
            }
            if (role.children && role.children.length > 0) {
               updatedRole.children = updatedRole.children.map((child) => (child.roleId === roleId ? {
                  ...child,
                  [field]: value,
               } : {
                  ...child,
               }));
            }
            // if (role.children && role.children.length > 0) {
            //    updatedRole.children = updatePermissions(role.children);
            // }
            return updatedRole;
         });
      };
      setUserRoles((prevRoles) => updatePermissions(prevRoles));
   };

   // Select all permissions for a specific role and its children
   const handleRoleSelectAll = (roleId, checked) => {
      const updatePermissionsRecursively = (roles) => {
         return roles.map((role) => {
            let updatedRole = { ...role };
            if (role.roleId === roleId) {
               updatedRole = {
                  ...updatedRole,
                  add: checked,
                  edit: checked,
                  deleteRole: checked,
                  view: checked,
                  print: checked,
               };

               // Also update children recursively
               if (updatedRole.children && updatedRole.children.length > 0) {
                  updatedRole.children = updatedRole.children.map((child) => ({
                     ...child,
                     add: checked,
                     edit: checked,
                     deleteRole: checked,
                     view: checked,
                     print: checked,
                  }));
               }
            } else if (role.children && role.children.length > 0) {
               updatedRole.children = updatePermissionsRecursively(role.children);
            }
            return updatedRole;
         });
      };

      setUserRoles((prevRoles) => updatePermissionsRecursively(prevRoles));
   };

   const handleSelectAll = useCallback(
      (e) => {
         const { checked } = e.target;
         setSelectAll(checked);
         setAddAll(checked);
         setEditAll(checked);
         setDeleteAll(checked);
         setPrintAll(checked);
         setViewAll(checked);
         const updatedRoles = setAllPermissionsTrue(userRoles, checked);
         setUserRoles(updatedRoles);
      },
      [userRoles],
   );

   // update add all roles
   const handleAddAll = useCallback(
      (e) => {
         const { checked } = e.target;
         setAddAll(checked);
         const updatedRoles = setAllActionPermissions(userRoles, 'add', checked);
         setUserRoles(updatedRoles);
      },
      [userRoles],
   );

   // update edit all roles
   const handleEditAll = useCallback(
      (e) => {
         const { checked } = e.target;
         setEditAll(checked);
         const updatedRoles = setAllActionPermissions(userRoles, 'edit', checked);
         setUserRoles(updatedRoles);
      },
      [userRoles],
   );

   // update delete all roles
   const handleDeleteAll = useCallback(
      (e) => {
         const { checked } = e.target;
         setDeleteAll(checked);
         const updatedRoles = setAllActionPermissions(userRoles, 'deleteRole', checked);
         setUserRoles(updatedRoles);
      },
      [userRoles],
   );

   // update print all roles
   const handlePrintAll = useCallback(
      (e) => {
         const { checked } = e.target;
         setPrintAll(checked);
         const updatedRoles = setAllActionPermissions(userRoles, 'print', checked);
         setUserRoles(updatedRoles);
      },
      [userRoles],
   );

   // update view all roles
   const handleViewAll = useCallback(
      (e) => {
         const { checked } = e.target;
         setViewAll(checked);
         const updatedRoles = setAllActionPermissions(userRoles, 'view', checked);
         setUserRoles(updatedRoles);
      },
      [userRoles],
   );

   const handleOnSubmit = async () => {
      try {
         // 1. Save current company's permissions before submitting
         const currentCompanyPermissions = flattenHierarchy(userRoles);
         const updatedUserRightsByCompany = {
            ...userRightsByCompany,
            [selectedCompanyId]: {
               ...currentCompanyPermissions,
               companyId: Number(selectedCompanyId),
            },
         };

         // 2. Convert to array of company rights
         const companiesWithPermissions = Object.values(updatedUserRightsByCompany).filter((company) => {
            // Check if any permission is set to true in this company
            return Object.values(company).some((right) => {
               if (typeof right !== 'object' || right === null) return false;
               return right.add || right.edit || right.deleteRole || right.print || right.view;
            });
         });

         // 3. Prepare the final payload
         const payload = {
            userId: selectedMember?.id || Number(userId),
            userRights: companiesWithPermissions,
         };

         // 4. Save the data
         if (processedData && onSaveUserRights) {
            onSaveUserRights(payload);
         } else {
            await dispatch(updateUserRights(payload));
            await dispatch(getUser());

            toast({
               title: 'Success',
               description: 'Permissions saved successfully!',
               status: 'success',
               duration: 3000,
               isClosable: true,
            });
            onClose();
         }
      } catch (error) {
         console.error('Error saving permissions:', error);
         toast({
            title: 'Error',
            description: error.message || 'Failed to save permissions.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   // Show loader when loading data
   if (loading || !companyList) {
      return (
         <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent>
               <ModalBody>
                  <Flex
                     position="fixed"
                     top="0"
                     left="0"
                     right="0"
                     bottom="0"
                     zIndex="9999"
                     justifyContent="center"
                     alignItems="center"
                     backgroundColor={overlayBg}
                     backdropFilter="blur(5px)">
                     <Image
                        src={loaderAnimation}
                        width={150}
                        height={150}
                        objectFit="cover"
                        style={{
                           position: 'absolute',
                           top: '50%',
                           left: '50%',
                           transform: 'translate(-50%, -50%)',
                        }}
                     />
                  </Flex>
               </ModalBody>
            </ModalContent>
         </Modal>
      );
   }

   return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="6xl" onCloseComplete={resetState}>
         <ModalOverlay />
         <ModalContent>
            <ModalHeader>Select Role</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
               <Flex width="300px" direction="row" mb={4} align="center">
                  <FormLabel htmlFor="company" mb={0} mr={2}>
                     Company
                  </FormLabel>
                  <Select
                     id="company"
                     placeholder="Select company"
                     onChange={handleCompChange}
                     value={selectedCompanyId}>
                     {companyList
                        ?.filter((comp) => comp.isShared !== true) // only non-shared
                        .map((comp) => (
                           <option key={comp.id || 'default'} value={comp.id || ''}>
                              {comp.name || 'Unknown Company'}
                           </option>
                        ))}
                  </Select>
               </Flex>

               {selectedCompanyId ? (
                  <Table variant="simple">
                     <Thead>
                        <Tr>
                           <Th>
                              <Checkbox mr={'5px'} onChange={(e) => handleSelectAll(e)} isChecked={selectAll} />
                              <Text as="span">Select All</Text>
                           </Th>
                           <Th>Name</Th>
                           <Th>
                              <Checkbox mr={'5px'} onChange={handleAddAll} isChecked={addAll} />
                              <Text as="span">Add</Text>
                           </Th>
                           <Th>
                              <Checkbox mr={'5px'} onChange={handleEditAll} isChecked={editAll} />
                              <Text as="span">Edit</Text>
                           </Th>
                           <Th>
                              <Checkbox mr={'5px'} onChange={handleDeleteAll} isChecked={deleteAll} />
                              <Text as="span">Delete</Text>
                           </Th>
                           <Th>
                              <Checkbox mr={'5px'} onChange={handlePrintAll} isChecked={printAll} />
                              <Text as="span">Print</Text>
                           </Th>
                           <Th>
                              <Checkbox mr={'5px'} onChange={handleViewAll} isChecked={viewAll} />
                              <Text as="span">View</Text>
                           </Th>
                        </Tr>
                     </Thead>
                     <Tbody>{userRoles.map((role) => renderRoleRow(role))}</Tbody>
                  </Table>
               ) : (
                  <Text>Please select a company to view and manage user rights.</Text>
               )}
            </ModalBody>
            <ModalFooter>
               <Button colorScheme="green" variant="ghost" mr={3} onClick={() => onClose()}>
                  Cancel
               </Button>
               <Button
                  colorScheme="green"
                  variant="solid"
                  mr={3}
                  onClick={() => handleOnSubmit()}
                  isDisabled={!selectedCompanyId}>
                  Save
               </Button>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default ManageUserRoleForm;
